import { IImportContext } from "@/components/ImportComponents/ImportContext";
import {
  ERROR_STATE,
  FINALIZED_STATE,
} from "@/pages/infrastructure/import/addToSystem";

import type {
  CreateEventsResponseBodyDto,
  CreateEventsResponseBodyItemFailedWrapperDto,
} from "server/api/index.js";

import { EVENT_KEY, EVENT_TIMESTAMP_KEY } from "./constants";

type BulkCreateEvent = {
  timestamp: string;
  customerId: string;
  pointId: string;
  weightKg: number;
};

type AddToSystemProps = {
  context: IImportContext;
  bulkCreateEvents: (input: {
    events: BulkCreateEvent[];
  }) => Promise<CreateEventsResponseBodyDto>;
};

const getEventsToUpload = ({
  context: { validationState },
}: AddToSystemProps): BulkCreateEvent[] => {
  const eventData = validationState[EVENT_KEY]!;

  const eventTimestampRows = eventData[EVENT_TIMESTAMP_KEY];

  const { customerId, containerId, weight } = eventData;

  return eventTimestampRows.map((timestampCell, i: number) => {
    const event: BulkCreateEvent = {
      timestamp: timestampCell.value,
      customerId: customerId[i].value,
      pointId: containerId[i].value,
      weightKg: Number(weight[i].value),
    };

    return event;
  });
};

const CHUNK_SIZE = 100;

const addCustomers = async (props: AddToSystemProps) => {
  const { setLoadingState, fullReset } = props.context;

  const events = getEventsToUpload(props);

  setLoadingState({
    importing: "importLabels:importingEvents",
    finalized: false,
  });

  const failedItems: Array<{ index: number; message: string }> = [];
  let currentChunkStart = 0;

  try {
    for (let i = 0; i < events.length; i += CHUNK_SIZE) {
      currentChunkStart = i;
      const response = await props.bulkCreateEvents({
        events: events.slice(i, i + CHUNK_SIZE),
      });

      response.items.forEach((item, j) => {
        if (item.type === "failed") {
          failedItems.push({
            index: i + j + 1,
            message: (item as CreateEventsResponseBodyItemFailedWrapperDto).item
              .error.message,
          });
        }
      });
    }

    if (failedItems.length > 0) {
      setLoadingState({ error: true, failedItems });
    } else {
      setLoadingState(FINALIZED_STATE);
      fullReset();
    }
  } catch {
    const lastRow = Math.min(currentChunkStart + CHUNK_SIZE, events.length);
    failedItems.push({
      index: currentChunkStart + 1,
      message: `Request failed — rows ${currentChunkStart + 1}–${lastRow} were not imported`,
    });
    setLoadingState({ ...ERROR_STATE, failedItems });
  }
};

export const addToSystem = (props: AddToSystemProps) => {
  const { selectedTemplate } = props.context;

  switch (selectedTemplate?.key) {
    case "events":
      addCustomers(props);
      break;

    default:
      break;
  }
};
